import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useRef } from "react";
import { Icon, Spinner } from "@/components/atoms";
import { clientBookingApi, useGetBulkImportQuery } from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppDispatch } from "@/redux/hooks";
import { openBulkImportReport } from "@/redux/slices/bulkImportReport/bulkImportReportSlice";
import { showSnackbar } from "@/utils";

/** How often to ask how far the import has got. Fast enough to feel live, slow enough to be free. */
const POLL_MS = 2000;

/** One toast id for the whole run, so progress updates in place instead of stacking. */
const SNACKBAR_ID = "bulk-import-progress";

/**
 * Every snackbar this feature raises waits to be dismissed. None of them expire.
 *
 * The Snackbar's 8-second default is pitched at confirming something the user has just clicked. Nothing
 * here is that: the run is deliberately in the background, the feature tells them they may walk away,
 * and each of these three states — still going, finished cleanly, finished with failures — reports on
 * something they are not watching. A result that expires while their attention is elsewhere leaves an
 * empty corner, which is indistinguishable from an import that never started.
 *
 * This applies to the clean result too. The earlier reasoning was that good news with nothing to press
 * shouldn't demand a dismissal — Justin's call is that being missed is the worse failure, and confirming
 * forty trips were created is worth a click to clear.
 */
const SNACKBAR_DURATION = Infinity;

interface BulkImportTrackerProps {
  /** The queued import to follow, or undefined when there's nothing running. */
  bulkId?: string;
  /** OBO: the client the import belongs to. */
  accountId?: string;
  /** Called once the run has finished and been reported, so the caller can forget it. */
  onSettled: () => void;
}

/**
 * Follows a queued bulk import and reports it (DEV-1215).
 *
 * Deliberately mounted OUTSIDE the wizard. The wizard's provider is keyed on `open`, so submitting
 * unmounts it and everything it held; the run outlives it, and so must whatever is watching.
 *
 * Renders nothing, ever. Progress goes to the Snackbar so the user can keep working, and a run that
 * finishes with something unresolved offers a way through to the report — which lives at its own URL
 * rather than in a modal here. One report, one place, whether you arrive from the Snackbar or from
 * the completion email days later.
 *
 * Closing the tab stops the polling, not the import. The worker finishes regardless, and the
 * completion email is what covers that case.
 */
export const BulkImportTracker = ({ bulkId, accountId, onSettled }: BulkImportTrackerProps) => {
  const dispatch = useAppDispatch();
  /**
   * The import already reported, so the finished state isn't announced on every poll.
   *
   * Cleared when tracking stops, not left set. Holding the last id forever meant that following the
   * SAME import again — which is exactly what a retry does — was silently ignored, and the retry
   * looked like it had done nothing.
   */
  const reported = useRef<string>();

  const { data } = useGetBulkImportQuery(bulkId ? { bulkId, accountId } : skipToken, {
    pollingInterval: bulkId ? POLL_MS : 0,
    // The worker is writing to this while we read it; a cached answer would freeze the progress line.
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!bulkId) reported.current = undefined;
  }, [bulkId]);

  useEffect(() => {
    if (!data || !bulkId) return;

    const done = data.status === "completed" || data.status === "failed";

    if (!done) {
      showSnackbar({
        title: "Importing bookings",
        message: `${data.booked_count + data.failed_count} of ${data.total_rows} processed`,
        timestamp: "In progress",
        /*
         * A spinner rather than the finished state's icon.
         *
         * The count only moves when a poll brings a new one, so for seconds at a time this toast was a
         * fixed icon beside a fixed number — indistinguishable from a run that had stalled. The
         * spinner is the only part that says "still going" between polls.
         *
         * `variant="light"` because the Snackbar is dark. Its icon slot sets `text-white`, which does
         * nothing here — Spinner colours itself with a BORDER, so the default dark variant rendered
         * border-primary on near-black and was invisible.
         */
        icon: <Spinner variant="light" className="mr-0" />,
        /*
         * Stays until the run ends, rather than expiring on a timer — see SNACKBAR_DURATION.
         *
         * It used to be re-issued on every poll with a few seconds' life, which assumed every poll
         * re-runs this effect. It doesn't: RTK Query hands back the SAME object when the response
         * hasn't changed, so an import that sits still — queued behind other work, or stuck — never
         * re-triggers, and the only snackbar the user gets vanishes after six seconds while the
         * import is very much still going.
         *
         * The completion branch below re-issues under the same id, which replaces this one.
         */
        duration: SNACKBAR_DURATION,
        id: SNACKBAR_ID,
      });
      return;
    }

    if (reported.current === bulkId) return;
    reported.current = bulkId;

    // Three numbers, not one. "Failed" and "never attempted" call for different things from the
    // reader, and folding the second into the first would tell someone their booking was refused
    // when nothing ever looked at it.
    const unresolved = data.failed_count + data.not_attempted_count;
    const summary = [
      `${data.booked_count} added`,
      data.failed_count > 0 && `${data.failed_count} failed`,
      data.not_attempted_count > 0 && `${data.not_attempted_count} not attempted`,
    ]
      .filter(Boolean)
      .join(", ");

    showSnackbar({
      title: unresolved > 0 ? "Import Finished" : "Import Successful",
      message:
        unresolved > 0 ? summary : `${data.booked_count} New Booking${data.booked_count === 1 ? "" : "s"} Added`,
      timestamp: "Just now",
      icon: <Icon name="ArchiveBook" size="md" />,
      id: SNACKBAR_ID,
      // Waits to be dismissed either way — see SNACKBAR_DURATION.
      duration: SNACKBAR_DURATION,
      /*
       * Both outcomes get the way in, because this snackbar is the ONLY route to the report from inside
       * the app — every other way in is a link in an email.
       *
       * A clean run used to get none, on the reasoning that the bookings are in the list so there is
       * nothing to press. That was decided before the report listed what it had created; it now shows a
       * booking number and fare per row, which is the one place a file is tied to the trips it produced.
       * Booking History cannot answer "which of these came from that upload".
       */
      actionText: "View",
      /*
       * Opens the report over whatever the user is doing, rather than navigating to its page.
       *
       * The whole premise of the feature is that you can start an import and walk away, so ending it
       * by throwing away the page someone had moved on to is the wrong note. The page still exists
       * and is still what the completion email links to — an email reader has nothing to be kept on.
       *
       * Dispatched rather than handled here because this toast outlives this component: it waits to
       * be dismissed, and for an operator the Book a Trip button that hosts this tracker only exists
       * on the Bookings page.
       */
      onActionClick: () => dispatch(openBulkImportReport({ bulkId, accountId })),
    });

    // The bookings behind were created by a worker, so nothing invalidated the list — do it here.
    dispatch(clientBookingApi.util.invalidateTags(["ClientBookings"]));

    onSettled();
  }, [data, bulkId, accountId, dispatch, onSettled]);

  return null;
};
